import React, { useState } from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import { ICandidateSourcingSearch, ICandidateSourcingFilters, FilterKey } from './types';
import Tag from '../Components/Tag/Tag';
import { Edit } from '@mui/icons-material';
import { EditFiltersModal } from './EditFiltersModal';
import { baseSecondaryButtonStyles, tableSearchButtonStyles } from '../shared/commonStyles';
import { theme } from '../../ThemeContext/ThemeObject';

export default function Filters({
  searchObject,
  setSearchObject,
  updateFilters,
  optionsCleared,
  isUpdatingFilters,
  onJobDialogOpen
}: {
  searchObject: ICandidateSourcingSearch;
  optionsCleared: boolean;
  setSearchObject: (searchObject: ICandidateSourcingSearch) => void;
  updateFilters: (filters: ICandidateSourcingFilters) => void;
  isUpdatingFilters: boolean;
  onJobDialogOpen: () => void;
}) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const filters = (searchObject.filters || {}) as ICandidateSourcingFilters;
  const filterOptions = optionsCleared ? {} : searchObject.filter_options || {};

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleFiltersChange = async (newFilters: ICandidateSourcingFilters) => {
    setSearchObject({
      ...searchObject,
      filters: newFilters
    });
    updateFilters(newFilters);
  };

  const handleRemoveFilter = async (key: FilterKey) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setSearchObject({
      ...searchObject,
      filters: newFilters
    });
    updateFilters(newFilters);
  };

  const handleRemoveSkill = async (skillGroupIndex: number, skillIndex: number) => {
    const newSkills = [...(filters.skills || [])];
    newSkills[skillGroupIndex] = newSkills[skillGroupIndex].filter(
      (_, index) => index !== skillIndex
    );
    if (newSkills[skillGroupIndex].length === 0) {
      newSkills.splice(skillGroupIndex, 1);
    }
    setSearchObject({
      ...searchObject,
      filters: { ...filters, skills: newSkills }
    });
    updateFilters({ ...filters, skills: newSkills });
  };

  const handleRemoveJobTitle = async (jobTitle: string) => {
    const newJobTitles = filters.job_titles?.filter((title) => title !== jobTitle) || [];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, job_titles: newJobTitles }
    });
    updateFilters({ ...filters, job_titles: newJobTitles });
  };

  const handleRemoveIndustry = async (industry: string) => {
    const newIndustries = filters.industries?.filter((i: string) => i !== industry) || [];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, industries: newIndustries }
    });
    updateFilters({ ...filters, industries: newIndustries });
  };

  const handleUpdateSkill = async (
    skillGroupIndex: number,
    skillIndex: number,
    newSkill: string
  ) => {
    const newSkills = [...(filters.skills || [])];
    newSkills[skillGroupIndex][skillIndex] = newSkill;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, skills: newSkills }
    });
    updateFilters({ ...filters, skills: newSkills });
  };

  const handleUpdateJobTitle = async (index: number, newJobTitle: string) => {
    const newJobTitles = [...(filters.job_titles || [])];
    newJobTitles[index] = newJobTitle;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, job_titles: newJobTitles }
    });
    updateFilters({ ...filters, job_titles: newJobTitles });
  };

  const handleUpdateIndustry = async (index: number, newIndustry: string) => {
    const newIndustries = [...(filters.industries || [])];
    newIndustries[index] = newIndustry;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, industries: newIndustries }
    });
    updateFilters({ ...filters, industries: newIndustries });
  };

  const delimiterFor = (index: number, array: string[] | null | undefined) => {
    return array && index < array.length - 1 ? ' or ' : '';
  };

  return (
    <Stack>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h3>Are these filters looking good?</h3>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={handleOpenEditModal}
            disabled={isUpdatingFilters}
            sx={{ ...tableSearchButtonStyles, height: '35px', paddingInline: theme.spacing(2) }}
          >
            Edit Filters
          </Button>
          <Button
            onClick={onJobDialogOpen}
            sx={{ ...baseSecondaryButtonStyles, paddingInline: theme.spacing(2) }}
            size="small"
          >
            Job Description
          </Button>
        </Stack>
      </Box>
      <Typography
        variant="body1"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
      >
        <strong>Looking for</strong>
        {filters.job_titles && filters.job_titles.length > 0 ? (
          <>
            {filters.job_titles.map((title, index) => (
              <React.Fragment key={`${title}-${index}`}>
                <Tag
                  label={title}
                  onDelete={() => handleRemoveJobTitle(title)}
                  options={filterOptions.job_titles || []}
                  onSelect={(option) => handleUpdateJobTitle(index, option)}
                  isInteractive={filterOptions.job_titles && filterOptions.job_titles.length > 0}
                  disabled={isUpdatingFilters}
                />
                {delimiterFor(index, filters.job_titles)}
              </React.Fragment>
            ))}
          </>
        ) : (
          'candidates'
        )}
        {filters.skills && filters.skills.length > 0 && (
          <>
            <strong>skilled in</strong>
            {filters.skills.map((skillGroup, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {groupIndex > 0 && ' AND '}
                {skillGroup.map((skill, skillIndex) => (
                  <React.Fragment key={skill}>
                    <Tag
                      label={skill}
                      onDelete={() => handleRemoveSkill(groupIndex, skillIndex)}
                      options={filterOptions.skills || []}
                      onSelect={(option) => handleUpdateSkill(groupIndex, skillIndex, option)}
                      isInteractive={filterOptions.skills && filterOptions.skills.length > 0}
                      disabled={isUpdatingFilters}
                    />
                    {delimiterFor(skillIndex, skillGroup)}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </>
        )}
        {filters.industries && filters.industries.length > 0 && (
          <>
            <strong> working in </strong>
            {filters.industries.map((industry, index) => (
              <React.Fragment key={industry}>
                <Tag
                  label={industry}
                  onDelete={() => handleRemoveIndustry(industry)}
                  options={filterOptions.industries || []}
                  onSelect={(option) => handleUpdateIndustry(index, option)}
                  isInteractive={filterOptions.industries && filterOptions.industries.length > 0}
                  disabled={isUpdatingFilters}
                />
                {delimiterFor(index, filters.industries)}
              </React.Fragment>
            ))}
          </>
        )}
        {!!filters.years_of_experience && (
          <>
            <strong>with</strong>
            <Tag
              label={`${filters.years_of_experience} years of experience`}
              onDelete={() => handleRemoveFilter('years_of_experience')}
              disabled={isUpdatingFilters}
            />
          </>
        )}
        {filters.location && (
          <>
            <strong>located in</strong>
            <Tag
              label={filters.location}
              onDelete={() => handleRemoveFilter('location')}
              disabled={isUpdatingFilters}
            />
          </>
        )}
      </Typography>
      <EditFiltersModal
        filterOptions={filterOptions}
        filters={filters}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onFiltersChange={handleFiltersChange}
      />
    </Stack>
  );
}
